import React, {useCallback, useEffect, useState} from 'react';
import {getPrompt, PromptType, updatePrompt} from '../../services/prompt.service';
import {ControlsWrapper, Header, StyledContainer} from './Prompts.styled';
import {useSnackbar} from '../../contexts/SnackbarContext';
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
    OutlinedInput
} from '@mui/material';
import {parseError} from "../../services/http.service";
import {useTranslation} from 'react-i18next';
import i18n from "../../i18n";
import {languages} from "../../config/languages";
import styled from "styled-components";
import FieldHelpSuffix from "../../components/common/FieldHelpSuffix";

const StyledFormControl = styled(FormControl)`
    width: 100%;
    max-width: none;
`;

const Prompts: React.FC = () => {
    const [selectedType, setSelectedType] = useState<PromptType>(PromptType.STANDARD);
    const [content, setContent] = useState('');
    const [originalContent, setOriginalContent] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState<string>(i18n.language || 'pl');

    const {showSnackbar} = useSnackbar();
    const {t} = useTranslation();

    const fetchPromptContent = useCallback(async () => {
        setIsLoading(true);
        try {
            const prompt = await getPrompt(selectedType, selectedLanguage);
            setContent(prompt.content);
            setOriginalContent(prompt.content);
        } catch (error) {
            console.error('Error fetching prompt:', error);
            showSnackbar(t('prompts.fetchError'), 'error');
        } finally {
            setIsLoading(false);
        }
    }, [selectedType, selectedLanguage, showSnackbar, t]);

    useEffect(() => {
        fetchPromptContent();
    }, [fetchPromptContent]);

    const handleSave = async () => {
        setIsLoading(true);
        try {
            await updatePrompt(selectedType, content, selectedLanguage);
            setOriginalContent(content);
            showSnackbar(t('prompts.saveSuccess'), 'success');
        } catch (error) {
            console.error('Error updating prompt:', error);
            showSnackbar(parseError(error), "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleTypeChange = (event: SelectChangeEvent<PromptType>) => {
        setSelectedType(event.target.value as PromptType);
    };

    const handleLanguageChange = (event: SelectChangeEvent) => {
        setSelectedLanguage(event.target.value as string);
    };

    const isContentChanged = content !== originalContent;

    return (
        <StyledContainer role="region" aria-labelledby="prompts-title">
            <Header>
                <Typography id="prompts-title" variant="h4" component="h2">{t('prompts.title')}</Typography>
            </Header>

            <ControlsWrapper>
                <StyledFormControl fullWidth variant="outlined">
                    <InputLabel id="prompt-type-select-label">{t('prompts.promptType')}</InputLabel>
                    <Select
                        labelId="prompt-type-select-label"
                        id="prompt-type-select"
                        value={selectedType}
                        label={t('prompts.promptType')}
                        onChange={handleTypeChange}
                        disabled={isLoading}
                        sx={{ '& .MuiSelect-icon': { right: 40 } }}
                        input={
                            <OutlinedInput
                                label={t('prompts.promptType')}
                                endAdornment={
                                    <FieldHelpSuffix
                                        asAdornment
                                        title={t('prompts.help.promptType')}
                                        ariaLabel={t('prompts.help.promptType') as string}
                                    />
                                }
                                sx={{ pr: '72px' }}
                            />
                        }
                    >
                        <MenuItem value={PromptType.STANDARD}>{t('prompts.standard')}</MenuItem>
                        <MenuItem value={PromptType.COMPLIANCE}>{t('prompts.compliance')}</MenuItem>
                    </Select>
                </StyledFormControl>

                <StyledFormControl fullWidth variant="outlined">
                    <InputLabel id="language-select-label">{t('prompts.language')}</InputLabel>
                    <Select
                        labelId="language-select-label"
                        id="language-select"
                        value={selectedLanguage}
                        label={t('prompts.language')}
                        onChange={handleLanguageChange}
                        disabled={isLoading}
                        sx={{ '& .MuiSelect-icon': { right: 40 } }}
                        input={
                            <OutlinedInput
                                label={t('prompts.language')}
                                endAdornment={
                                    <FieldHelpSuffix
                                        asAdornment
                                        title={t('prompts.help.language')}
                                        ariaLabel={t('prompts.help.language') as string}
                                    />
                                }
                                sx={{ pr: '72px' }}
                            />
                        }
                    >
                        {languages.map(lang => (
                            <MenuItem key={lang.code} value={lang.code}>{lang.label}</MenuItem>
                        ))}
                    </Select>
                </StyledFormControl>
            </ControlsWrapper>

            <Box 
                sx={{position: 'relative'}}
                aria-live="polite"
                aria-atomic={true}
                aria-busy={isLoading}
            >
                <TextField
                    multiline
                    rows={15}
                    fullWidth
                    variant="outlined"
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    disabled={isLoading}
                    inputProps={{ 'aria-label': t('prompts.tableOrForm.ariaLabel') as string }}
                />
                <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                    <FieldHelpSuffix
                        title={t('prompts.help.content')}
                        ariaLabel={t('prompts.help.content') as string}
                    />
                </Box>
                {isLoading && (
                    <CircularProgress
                        size={40}
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            marginTop: '-20px',
                            marginLeft: '-20px',
                        }}
                        aria-hidden={true}
                    />
                )}
                {isLoading && (
                    <Box role="status" aria-live="polite" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(1px, 1px, 1px, 1px)' }}>
                        {t('prompts.loading')}
                    </Box>
                )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' }, mt: 2 }}>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!isContentChanged || isLoading}
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                    aria-label={t('prompts.save.ariaLabel') as string}
                >
                    {t('prompts.save')}
                </Button>
            </Box>
        </StyledContainer>
    );
};

export default Prompts;
